import type { BillboardListItem } from '@/features/billboards/utils/billboardsListUtils';
import { BillboardListCard } from '@/features/billboards/components/list/BillboardListCard';

type BillboardListGridProps = {
  items: BillboardListItem[];
  isPending: boolean;
  isError?: boolean;
  errorMessage?: string;
  detailBasePath?: '/screens' | '/billboard';
  onRetry?: () => void;
};

export function BillboardListGrid({
  items,
  isPending,
  isError = false,
  errorMessage,
  detailBasePath = '/billboard',
  onRetry,
}: BillboardListGridProps) {
  if (isPending) {
    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {Array.from({ length: 8 }, (_, index) => (

          <div
            key={index}
            className="overflow-hidden rounded-[24px] border border-[#ECEAF5] bg-white shadow-sm"
          >
            <div className="aspect-[16/10] animate-pulse bg-[#F3F2F9]" />
            <div className="space-y-3 p-4">
              <div className="h-4 w-3/4 animate-pulse rounded bg-[#F3F2F9]" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-[#F3F2F9]" />
              <div className="h-12 animate-pulse rounded-xl bg-[#F3F2F9]" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (

      <div className="flex flex-col items-center justify-center rounded-[28px] border border-dashed border-[#F3C4CD] bg-white px-6 py-16 text-center">
        <p className="text-lg font-bold text-[#3A3660]">Couldn’t load screens</p>
        <p className="mt-2 max-w-sm text-sm text-[#6A6688]">
          {errorMessage ||
            'Something went wrong while fetching screens. Try again.'}
        </p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="mt-5 rounded-[14px] border border-[#D9D3F0] bg-white px-5 py-2.5 text-sm font-bold text-[#2B3E8A] transition hover:bg-[#F4F2FB]"
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }

  if (!items.length) {
    return (

      <div className="flex flex-col items-center justify-center rounded-[28px] border border-dashed border-[#D9D3F0] bg-white px-6 py-16 text-center">
        <p className="text-lg font-bold text-[#3A3660]">No screens found</p>
        <p className="mt-2 max-w-sm text-sm text-[#6A6688]">
          Try another city, clear filters, or search with a different screen name.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {items.map((item, index) => (
        <BillboardListCard
          key={item._id || item.id || index}
          item={item}
          priority={index < 4}
          detailBasePath={detailBasePath}
        />
      ))}
    </div>
  );
}
